import { FormFields, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import {
  PricingEditorReducerState,
  UsageTierType,
  BillingFrequency,
  PriceEditorTier,
  PriceEditorPercentageTier
} from 'common/drawers/PricingEditor/domain'
import { defaultAvailableFrequenciesOptions } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { PRICING_MODEL_OPTIONS } from '@sequencehq/utils'
import { useMemo } from 'react'
import { useCommonFieldConfig } from '../common/useCommonFieldConfig'
import { useSyncFormValidity } from '../common/useSyncFormValidity'
import { Currency } from '@sequencehq/api'
import { useUsageMetricParameterFieldConfig } from '../common/UsageMetrics/useUsageMetricParameterFieldConfig'
import {
  percentageTiersValidator,
  tiersValidator
} from 'common/drawers/PricingEditor/utils/validators/tiers.validators'
import { commonPriceDetails } from '../common/ReadOnlyDetails/readOnlyDetails.utils'
import { useUsageMetrics } from '../../../communication/external/useUsageMetrics'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { getBillingFrequencyMultiplier } from 'common/drawers/PricingEditor/utils/getBillingFrequencyMultiplier'
import { floatSafeMultiply, enforceMinimumPrecision } from '@sequencehq/utils'
import { usageMetricIdValidator } from 'common/drawers/PricingEditor/utils/validators/usageMetricId.validators'
import { dequal } from 'dequal'
import { pick } from 'lodash/fp'

type CommonFields =
  PricingEditorReducerState['data']['pricingEditorData']['common']
type VolumePriceFields =
  PricingEditorReducerState['data']['pricingEditorData']['VOLUME']

type VolumePriceFormFieldConfig = Omit<
  FormFields<CommonFields>,
  'pricingModel'
> & {
  pricingModel: FormFields<CommonFields>['pricingModel'] & {
    description: string
  }
} & {
  currency: FormFields<CommonFields>['currency'] & {
    hidden: boolean
  }
} & FormFields<VolumePriceFields>

type UseVolumePriceForm = () => {
  fieldsConfig: VolumePriceFormFieldConfig
  currency: Currency
  priceDetails: { label: string; value: string }[]
}

const availableUsageTierTypes: { label: string; value: UsageTierType }[] = [
  {
    label: 'Fixed',
    value: 'FIXED'
  },
  {
    label: 'Percentage',
    value: 'PERCENTAGE'
  }
]

const adjustTierFlatFlatFee =
  <T extends { flatFee: string }>(ratio: number) =>
  (tier: T): T => {
    const flatFeePrecision = tier.flatFee.split('.')[1]?.length ?? 0
    const flatFeeTargetPrecision = Math.max(flatFeePrecision, 2)

    return {
      ...tier,
      flatFee: enforceMinimumPrecision(2)(
        floatSafeMultiply(+tier.flatFee, ratio, flatFeeTargetPrecision)
      )
    }
  }

const adjustVolumePrice = (params: {
  ratio: number
  tiers: PriceEditorTier[]
  percentageTiers: PriceEditorPercentageTier[]
}) => {
  /**
   * We format both of them on a change, since we don't blank them out
   * on a calculation change either, since we won't want to lose potentially
   * large amounts of tiers by accident! Therefore, keeping both tier types
   * in sync makes sense.
   */
  return {
    tiers: params.tiers.map(adjustTierFlatFlatFee(params.ratio)),
    percentageTiers: params.percentageTiers.map(
      adjustTierFlatFlatFee(params.ratio)
    )
  }
}

export const useVolumePriceForm: UseVolumePriceForm = () => {
  const pricingEditorContext = usePricingEditorDomainContext()
  const flags = useFeatureFlags()
  const commonFields = useCommonFieldConfig<VolumePriceFields & CommonFields>()
  const parameterFields = useUsageMetricParameterFieldConfig<
    VolumePriceFields & CommonFields
  >(
    pricingEditorContext.queries.rawData.data.pricingEditorData.VOLUME
      .usageMetricId,
    () => false
  )

  /**
   * Allow for the on demand billing frequency if the flag is enabled, or the value has already been set (i.e. the flag is now disabled,
   * but a price was set using it). However, when the flag is disabled this will act as trap door state, and will cause the value to
   * disappear from selection once changed.
   */
  const onDemandFrequencyAvailable =
    pricingEditorContext.queries.rawData.data.pricingEditorData.VOLUME
      .billingFrequency === 'ON_DEMAND' || flags.useOnDemandBillingFrequency

  const perEventFrequencyAvailable =
    pricingEditorContext.queries.rawData.data.pricingEditorData.VOLUME
      .billingFrequency === 'PER_EVENT' || flags.usePerEventBilling

  const { usageMetrics } = useUsageMetrics()
  const validateUsageMetricId = usageMetricIdValidator(usageMetrics)

  const { fields, queries, mutators } = useForm({
    value: {
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.common,
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.VOLUME
    },
    reinitializeOnValueChange: (params: {
      newData: VolumePriceFields & CommonFields
      prevData: (VolumePriceFields & CommonFields) | undefined
    }) => {
      const propertiesToCompare = pick([
        'productId',
        'listPriceId',
        'usageMetricId'
      ])
      return !dequal(
        propertiesToCompare(params.newData),
        propertiesToCompare(params.prevData)
      )
    },
    fieldConfiguration: [
      ...commonFields.fields,
      {
        property: 'billingFrequency',
        options: [
          ...defaultAvailableFrequenciesOptions,
          ...(onDemandFrequencyAvailable
            ? [
                {
                  value: 'ON_DEMAND',
                  label: 'On demand'
                }
              ]
            : []),
          ...(perEventFrequencyAvailable
            ? [
                {
                  value: 'PER_EVENT',
                  label: 'Per event'
                }
              ]
            : [])
        ],
        validation: [required]
      },
      {
        property: 'usageMetricId',
        validation: [required, validateUsageMetricId]
      },
      {
        property: 'tiers',
        disabled: ctx => ctx.formData.usageTierType !== 'FIXED',
        validation: [required, tiersValidator]
      },
      {
        property: 'percentageTiers',
        disabled: ctx => ctx.formData.usageTierType !== 'PERCENTAGE',
        validation: [required, percentageTiersValidator]
      },
      {
        property: 'usageTierType',
        validation: [required],
        options: availableUsageTierTypes
      },
      {
        property: 'includePercentageLimits'
      },
      ...parameterFields.fields
    ],
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    disabled:
      !pricingEditorContext.queries.availableFeatures.common.form.available
        .enabled,
    onChange: newData => {
      const { name, pricingModel, currency, ...rest } = newData

      pricingEditorContext.mutators.common.updatePricingEditorData({
        common: {
          name,
          currency
        },
        VOLUME: rest
      })
    }
  })

  /**
   * A usage metric's parameters come from the metric, so their property names
   * aren't known up front. They all render in the additional fields section.
   */
  const parameterFieldNames = parameterFields.fields.map(
    field => field.property
  )

  useSyncFormValidity({
    isFormValid: queries.isValid,
    formValidationResults: queries.validationResults,
    additionalFields: [
      'billingFrequency',
      'usageMetricId',
      ...parameterFieldNames
    ]
  })

  const enhancedFields = useMemo(() => {
    return {
      ...fields,
      pricingModel: {
        ...fields.pricingModel,
        description:
          PRICING_MODEL_OPTIONS.find(
            option => option.value === queries.formData.pricingModel
          )?.description ?? ''
      },
      currency: {
        ...fields.currency,
        hidden: Boolean(
          pricingEditorContext.queries.rawData.configuration.currency
        )
      },
      billingFrequency: {
        ...fields.billingFrequency,
        /**
         * Adjust the tier prices on a frequency change.
         */
        onChange: (newValue: BillingFrequency) => {
          const originalBillingFrequency = queries.formData.billingFrequency
          const ratio = getBillingFrequencyMultiplier({
            fromFrequency: originalBillingFrequency,
            toFrequency: newValue
          })

          mutators.updateFormData({
            billingFrequency: newValue,
            ...adjustVolumePrice({
              ratio,
              tiers: queries.formData.tiers,
              percentageTiers: queries.formData.percentageTiers
            })
          })
        }
      }
    }
  }, [
    fields,
    queries.formData.pricingModel,
    queries.formData.billingFrequency,
    queries.formData.tiers,
    queries.formData.percentageTiers,
    pricingEditorContext.queries.rawData.configuration.currency,
    mutators
  ])

  const priceDetails = useMemo(() => {
    return [
      ...commonPriceDetails({
        taxCategoryName:
          pricingEditorContext.queries.rawData.data.product.taxCategoryName,
        revenueRecognitionMethod:
          pricingEditorContext.queries.rawData.data.product
            .revenueRecognitionMethod,
        pricingModel: 'VOLUME',
        billingFrequency: queries.formData.billingFrequency,
        billingType: queries.formData.billingType
      }),
      {
        label: 'Usage metric',
        value:
          usageMetrics.find(
            metric => metric.id === queries.formData.usageMetricId
          )?.label ?? 'Not selected'
      },
      {
        label: 'Type',
        value:
          fields.usageTierType.options.find(
            opt => opt.value === queries.formData.usageTierType
          )?.label ?? 'Not selected'
      }
    ]
  }, [
    usageMetrics,
    fields.usageTierType.options,
    queries.formData.billingFrequency,
    queries.formData.billingType,
    queries.formData.usageMetricId,
    queries.formData.usageTierType,
    pricingEditorContext.queries.rawData.data.product.taxCategoryName,
    pricingEditorContext.queries.rawData.data.product.revenueRecognitionMethod
  ])

  return {
    fieldsConfig: enhancedFields,
    currency: queries.formData.currency,
    priceDetails
  }
}
